import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    const landing = await createLanding(organizationId, name.trim(), formId || undefined);
    setSaving(false);
    setCreating(false);
    setName('');
    setFormId('');
    navigate(`/app/landings/${landing.id}/editor`);
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-958" data-mp="26jx8q13le59j" />} onClick={() => setCreating(true)} data-id="element-957" data-mp="26kf0olj81xbn">
            Nueva landing
          </Button>} data-id="element-956" data-mp="v6utpah55nrh" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-960" data-mp="2hn5vnst9p00" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-961" data-mp="bitoq3ixnbwf">Crear landing</Button>} data-id="element-959" data-mp="2gu99fnkgfvl" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-962" data-mp="3jy55hbre7r0t">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-963" data-mp="31517je756s0e">
                <div className="flex items-start justify-between gap-3" data-id="element-964" data-mp="1bonrt3vdzys7">
                  <div className="min-w-0" data-id="element-965" data-mp="1gg6y31g3bwx4">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-966" data-mp="1gi0208zbk31f">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-967" data-mp="1ver2ymon5mt5">
                      /landing/{landing.slug}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-969" data-mp="1qecz7gu2oz0x">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-970" data-mp="u934ho4cwicx" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-971" data-mp="lhknxc3i00jb" />,
              onSelect: () => window.open(`/landing/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-972" data-mp="196n3meeknjb6" />,
              onSelect: () => navigator.clipboard?.writeText(`${window.location.origin}/landing/${landing.slug}`)
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-973" data-mp="3oveyc9apg0zq" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Despublicar' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-974" data-mp="3ergq18pc8kj5" />,
              onSelect: () => setLandingStatus(landing.id, landing.status === 'publicada' ? 'despublicada' : 'publicada')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-975" data-mp="1ghodx9fhyf2" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-968" data-mp="1lg1lgv0nq4df" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-976" data-mp="28662qvxs4est">
                  <div data-id="element-977" data-mp="2d3xqrevazgrq">
                    <dt className="text-xs text-muted-foreground" data-id="element-978" data-mp="2d58vpi0hiukf">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-979" data-mp="2539ln6jgw0sz">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-980" data-mp="20585ouhc8hlq">
                    <dt className="text-xs text-muted-foreground" data-id="element-981" data-mp="205q705iuxm0i">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-982" data-mp="2f1z4b1j6tpaa">{landing.views}</dd>
                  </div>
                  <div data-id="element-983" data-mp="2a1l0jvomd1i2">
                    <dt className="text-xs text-muted-foreground" data-id="element-984" data-mp="2a1b7fz8a2e85">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-985" data-mp="2o12oy9y06cqw">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-986" data-mp="2xsqaju7vozkg">
                    <dt className="text-xs text-muted-foreground" data-id="element-987" data-mp="2xqvqei4flbhf">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-988" data-mp="2ssu6juz6l84w">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-989" data-mp="36t50j6vsf2ua">
                  <Tag data-id="element-990" data-mp="32suu5t432aew">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-991" data-mp="3lf2dsoic0un2">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-993" data-mp="3hdyvzwgq4oj7">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-994" data-mp="3hcns7wna3gs3">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim()} data-id="element-995" data-mp="32nrqehbtezzx">
              Crear
            </Button>
          </div>} data-id="element-992" data-mp="3qdvroi1j6nnr">
        <div className="space-y-4" data-id="element-996" data-mp="2tp7cdth79ei1">
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-997" data-mp="2tq8zwxfejtgn" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-998" data-mp="2opbjapa1j7h9" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-999" data-mp="2v5v4ubgt5rcb" />
    </>;
}